/**
 * Largest-Triangle-Three-Buckets downsampling (Steinarsson 2013): keeps the first and last point and, in each bucket,
 * the point that makes the largest triangle with the previous pick and the next bucket's average, so peaks and
 * troughs survive (UX-SPEC §15 UX-PERF-08, ENGINE-SPEC §11.7). Output length is at most `threshold` (the input is
 * returned as is when it is already short enough).
 */
export function lttb(
	x: ArrayLike<number>,
	y: ArrayLike<number>,
	threshold: number
): { x: Float32Array; y: Float32Array } {
	const n = Math.min(x.length, y.length);
	if (threshold >= n || threshold < 3) {
		const m = threshold < 3 && n > 2 ? 2 : n;
		if (m === n) {
			const ox = new Float32Array(n);
			const oy = new Float32Array(n);
			for (let i = 0; i < n; i++) {
				ox[i] = x[i] as number;
				oy[i] = y[i] as number;
			}
			return { x: ox, y: oy };
		}
		return {
			x: Float32Array.of(x[0] as number, x[n - 1] as number),
			y: Float32Array.of(y[0] as number, y[n - 1] as number),
		};
	}
	const ox = new Float32Array(threshold);
	const oy = new Float32Array(threshold);
	const every = (n - 2) / (threshold - 2);
	let a = 0;
	ox[0] = x[0] as number;
	oy[0] = y[0] as number;
	for (let i = 0; i < threshold - 2; i++) {
		// average of the next bucket
		const nextStart = Math.floor((i + 1) * every) + 1;
		const nextEnd = Math.min(Math.floor((i + 2) * every) + 1, n);
		let avgX = 0;
		let avgY = 0;
		for (let j = nextStart; j < nextEnd; j++) {
			avgX += x[j] as number;
			avgY += y[j] as number;
		}
		const len = Math.max(1, nextEnd - nextStart);
		avgX /= len;
		avgY /= len;
		// the current bucket
		const start = Math.floor(i * every) + 1;
		const end = Math.floor((i + 1) * every) + 1;
		const ax = x[a] as number;
		const ay = y[a] as number;
		let best = -1;
		let pick = start;
		for (let j = start; j < end; j++) {
			const area = Math.abs(
				(ax - avgX) * ((y[j] as number) - ay) - (ax - (x[j] as number)) * (avgY - ay)
			);
			if (area > best) {
				best = area;
				pick = j;
			}
		}
		ox[i + 1] = x[pick] as number;
		oy[i + 1] = y[pick] as number;
		a = pick;
	}
	ox[threshold - 1] = x[n - 1] as number;
	oy[threshold - 1] = y[n - 1] as number;
	return { x: ox, y: oy };
}
